import React, { useState, useEffect } from "react";
import {
  FaStar,
  FaAward,
  FaTheaterMasks,
  FaIdBadge,
  FaPoll,
  FaInfoCircle,
} from "react-icons/fa";
import { Button } from "@jammable/ui-core";
import { TextArea } from "@jammable/ui-components/Forms";
import VoiceImage from "../VoiceImage";
import { getAuth } from "firebase/auth";
import { Spinner } from "@jammable/ui-components/Loader";
import { useQuery } from "react-query";
import axios from "axios";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import { useModal } from "../../context/ModalContext";

const RATING_CRITERIA = [
  {
    name: "Quality",
    description: "How clear and artifact-free is the voice output?",
    icon: FaAward,
  },
  {
    name: "Versatility",
    description: "How well does the voice adapt to different styles?",
    icon: FaTheaterMasks,
  },
  {
    name: "Authenticity",
    description: "How closely does it match the original voice source?",
    icon: FaIdBadge,
  },
  {
    name: "Overall",
    description: "Your overall satisfaction with this voice model.",
    icon: FaPoll,
  },
];

// Reusable component for displaying a single rating criterion
const RatingCriterionDisplay = ({
  criterion,
  rating,
  hoverRating,
  isReadOnly,
  onRatingChange,
  onMouseEnter,
  onMouseLeave,
}) => {
  const IconComponent = criterion.icon;
  const isOverall = criterion.name === "Overall";

  return (
    <React.Fragment>
      {isOverall && (
        <div className="tw-border-t-2 tw-border-b-0 tw-border-x-0 tw-border-solid tw-border-black/10"></div>
      )}
      <div
        className={`tw-flex sm:tw-items-center tw-justify-between tw-gap-3 ${isOverall ? "tw-pt-0" : ""} ${isReadOnly ? "" : "tw-truncate"}`}
      >
        <div
          className={`sm:tw-flex-1 sm:tw-min-w-0 ${isReadOnly ? "" : "tw-truncate tw-text-wrap"}`}
        >
          <div className="tw-flex tw-items-center tw-gap-2">
            <IconComponent className="tw-text-gray-400" size={18} />
            <span
              className="tw-text-base tw-uppercase tw-tracking-widest sm:tw-text-lg tw-font-black tw-block"
              title={criterion.name}
            >
              {criterion.name}
            </span>
          </div>
          <div className="tw-text-xs sm:tw-text-sm tw-text-gray-400 tw-text-wrap">
            {criterion.description}
          </div>
        </div>
        <div
          className={`tw-flex tw-items-center tw-space-x-1 tw-bg-gray-700 tw-px-3 tw-py-1.5 tw-rounded-full ${isReadOnly ? "tw-flex-shrink-0" : "tw-w-max tw-h-max"}`}
          onMouseLeave={
            !isReadOnly ? () => onMouseLeave(criterion.name) : undefined
          }
        >
          {[...Array(5)].map((_, starIndex) => {
            const ratingValue = starIndex + 1;
            const isFilled = isReadOnly
              ? ratingValue <= rating
              : ratingValue <= (hoverRating || rating);
            return (
              <FaStar
                key={starIndex}
                size={24}
                className={`
                  ${isReadOnly ? "" : "tw-cursor-pointer tw-transition-colors tw-duration-150 ease-in-out hover:tw-text-yellow-300"}
                  ${isFilled ? "tw-text-yellow-400" : "tw-text-gray-500"}
                `}
                onClick={
                  !isReadOnly
                    ? () => onRatingChange(criterion.name, ratingValue)
                    : undefined
                }
                onMouseEnter={
                  !isReadOnly
                    ? () => onMouseEnter(criterion.name, ratingValue)
                    : undefined
                }
              />
            );
          })}
        </div>
      </div>
    </React.Fragment>
  );
};

const fetchEligibility = async (modelId) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    // Handle case where user is not logged in, perhaps throw an error or return default state
    throw new Error("User not authenticated");
  }
  const token = await auth.currentUser.getIdToken();
  const response = await fetch(
    `/api/review/checkReviewStatus?modelId=${modelId}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return data; // Expecting { eligible: boolean }
};

const submitReview = async (reviewData) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }
  const token = await auth.currentUser.getIdToken();
  const { data } = await axios.post("/api/review/submitReview", reviewData, {
    headers: {
      Authorization: token,
    },
  });
  return data;
};

const RateVoiceModal = ({ model }) => {
  const [ratings, setRatings] = useState(
    RATING_CRITERIA.reduce((acc, criterion) => {
      acc[criterion.name] = 0;
      return acc;
    }, {}),
  );
  const [hoverRatings, setHoverRatings] = useState(
    RATING_CRITERIA.reduce((acc, criterion) => {
      acc[criterion.name] = 0;
      return acc;
    }, {}),
  );
  const [feedback, setFeedback] = useState("");
  const { closeModal } = useModal();
  const queryClient = useQueryClient();

  const {
    data: eligibilityData,
    isLoading: eligibilityLoading,
    error: eligibilityError,
  } = useQuery(["eligibility", model._id], () => fetchEligibility(model._id), {
    // Optional: Configure caching, refetching, etc.
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    onError: (e) => {
      console.error("Failed to fetch review eligibility:", e);
      // Optionally set an error state or show a toast message
    },
    // Keep previous data while refetching to avoid UI flashes
    keepPreviousData: true,
  });

  // Pre-fill form if review data exists
  useEffect(() => {
    if (eligibilityData?.hasReviewed && eligibilityData?.review) {
      setRatings(eligibilityData.review.ratings);
      setFeedback(eligibilityData.review.feedback || "");
    }
  }, [eligibilityData]);

  const mutation = useMutation(submitReview, {
    onSuccess: (data) => {
      toast.success(data.message || "Review submitted successfully!");
      // Optionally invalidate queries that might be affected, e.g., refetch eligibility
      queryClient.invalidateQueries(["eligibility", model._id]);
      closeModal("rate-voice-modal"); // Assuming modal key is 'rate-voice-modal'
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to submit review. Please try again.";
      toast.error(errorMessage);
      console.error("Failed to submit review:", error);
    },
  });

  const handleRating = (criterionName, rating) => {
    setRatings((prevRatings) => ({
      ...prevRatings,
      [criterionName]: rating,
    }));
  };

  const handleMouseEnter = (criterionName, rating) => {
    setHoverRatings((prevHoverRatings) => ({
      ...prevHoverRatings,
      [criterionName]: rating,
    }));
  };

  const handleMouseLeave = (criterionName) => {
    setHoverRatings((prevHoverRatings) => ({
      ...prevHoverRatings,
      [criterionName]: 0,
    }));
  };

  const handleSubmit = async () => {
    // Basic validation: Check if all ratings are provided (not 0)
    const allRated = RATING_CRITERIA.every(
      (criterion) => ratings[criterion.name] > 0,
    );
    if (!allRated) {
      toast.error("Please rate all criteria before submitting.");
      return;
    }

    const reviewData = {
      modelId: model._id,
      ratings,
      feedback,
    };
    mutation.mutate(reviewData);
  };

  if (eligibilityLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-p-8 tw-min-h-[150px]">
        {/* Simple Spinner */}
        <Spinner className="tw-border-3" size={6} />
      </div>
    );
  }

  if (eligibilityError) {
    return (
      <div className="tw-p-4 tw-bg-red-100 tw-border tw-border-red-300/30 tw-rounded-3xl tw-text-red-700">
        Could not check review eligibility. Please try again later.
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-truncate tw-text-wrap">
      <div className="tw-flex tw-items-start tw-gap-3 tw-mb-6 tw-w-full">
        <VoiceImage
          model={model}
          size={60}
          className="tw-rounded-full tw-border-2 tw-border-solid tw-border-white tw-flex-shrink-0"
        />
        <div className="tw-w-full tw-truncate">
          <h2 className="tw-text-xl tw-font-black tw-mt-0 tw-mb-0 tw-flex tw-items-center tw-gap-2 tw-truncate tw-w-full">
            <div className="tw-text-xs tw-text-black tw-flex tw-items-center tw-justify-center tw-px-1.5 tw-py-0.5 tw-font-black tw-rounded-full tw-bg-gradient-to-r tw-from-blue-200 tw-to-cyan-200">
              BETA
            </div>
            <span className="tw-truncate">{`Review ${model?.artistname}`}</span>
          </h2>
          <div className="tw-text-sm tw-text-gray-400 tw-text-wrap">
            Help us improve voice quality by rating models across different
            criteria. Your feedback is valuable!
          </div>
        </div>
      </div>

      {/* === Already Reviewed State === */}
      {eligibilityData?.hasReviewed && (
        <>
          <div className="tw-mb-6 tw-p-4 tw-bg-blue-100 tw-border tw-border-blue-300/30 tw-rounded-3xl tw-flex tw-items-center tw-gap-3">
            <FaInfoCircle
              className="tw-text-blue-700 tw-flex-shrink-0"
              size={20}
            />
            <div className="tw-text-sm tw-text-blue-700 tw-font-semibold">
              You have already submitted a review for this voice model.
            </div>
          </div>
          <div className="tw-space-y-5 tw-opacity-70">
            {RATING_CRITERIA.map((criterion) => (
              <RatingCriterionDisplay
                key={criterion.name}
                criterion={criterion}
                rating={ratings[criterion.name]}
                isReadOnly={true}
                // Pass dummy/null handlers for read-only state
                hoverRating={0}
                onRatingChange={() => {}}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
              />
            ))}
          </div>
          <div className="tw-mt-6">
            <TextArea
              id="feedback-textarea"
              value={feedback}
              placeholder="Your previously submitted feedback."
              className="tw-w-full tw-opacity-70" // Added opacity
              rows={3}
              disabled // Disable text area
            />
          </div>
          {/* No submit button needed */}
        </>
      )}

      {/* === Eligible to Review State === */}
      {!eligibilityData?.hasReviewed && eligibilityData?.eligible && (
        <>
          <div className="tw-space-y-5">
            {RATING_CRITERIA.map((criterion) => (
              <RatingCriterionDisplay
                key={criterion.name}
                criterion={criterion}
                rating={ratings[criterion.name]}
                hoverRating={hoverRatings[criterion.name]}
                isReadOnly={false}
                onRatingChange={handleRating}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            ))}
          </div>
          <div className="tw-mt-6">
            <TextArea
              id="feedback-textarea"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Provide any additional comments here..."
              className="tw-w-full"
              rows={3}
            />
          </div>
          <div className="tw-mt-2 tw-flex tw-justify-center">
            <Button
              className="tw-rounded-full tw-w-full"
              onClick={handleSubmit}
              disabled={mutation.isLoading} // Disable button when submitting
            >
              {mutation.isLoading ? (
                <Spinner className="tw-border-3 tw-border-white" size={4} />
              ) : (
                "Submit Review"
              )}
            </Button>
          </div>
        </>
      )}

      {/* === Not Eligible Yet State === */}
      {!eligibilityData?.hasReviewed && !eligibilityData?.eligible && (
        <div className="tw-mt-6 tw-p-4 tw-bg-purple-100 tw-border tw-border-purple-300/30 tw-rounded-3xl tw-flex tw-items-center tw-gap-3">
          <FaInfoCircle
            className="tw-text-purple-700 tw-flex-shrink-0"
            size={20}
          />
          <div className="tw-text-sm tw-text-purple-700 tw-font-semibold tw-text-wrap">
            You are not yet eligible to review this voice model. Reviews are
            typically enabled after using a model a few times.
          </div>
        </div>
      )}
    </div>
  );
};

export default RateVoiceModal;
